import { Check, Minus } from 'lucide-react';
import React from 'react';
import { Checkbox as AriaCheckbox, type CheckboxProps as AriaCheckboxProps } from 'react-aria-components';

import { classnames } from '@/utils/classnames.ts';

// `value`/`onChange` use a boolean like Switch, so Form.Field can bind it like any other field (react-aria's own
// `value` is the string submitted with a native form).
export interface CheckboxProps extends Omit<AriaCheckboxProps, 'value' | 'children' | 'className'> {
    value?: boolean;
    className?: string;
    children?: React.ReactNode;
    // Small for a line in fine print, e.g. accepting the terms above a form's button.
    size?: 'md' | 'sm';
}

export const checkboxClassName = (className?: string) =>
    classnames(
        'group inline-flex min-w-0 cursor-default items-center gap-3 outline-none data-[disabled]:not-data-[selected]:opacity-50',
        className
    );

// The box and the label inside a react-aria Checkbox, also one of a CheckboxGroupField's.
export const CheckboxContent = ({ children, size = 'md' }: { children?: React.ReactNode; size?: 'md' | 'sm' }) => (
    <>
        <span
            className={classnames(
                'grid shrink-0 place-items-center *:col-start-1 *:row-start-1 border-2 border-text-subtle bg-surface text-on-primary transition-colors',
                // Margins as high as the small text's line, so it sits on its first line also where the label wraps.
                size === 'sm' ? 'my-0.5 size-4 rounded' : 'size-5 rounded-md',
                'group-data-[selected]:border-primary group-data-[selected]:bg-primary group-data-[indeterminate]:border-primary group-data-[indeterminate]:bg-primary',
                // Ticked and not to be changed, e.g. what's a source already: the ticked box in grey, not faded, so it
                // doesn't read as missing.
                'group-data-[disabled]:group-data-[selected]:border-text-subtle group-data-[disabled]:group-data-[selected]:bg-text-subtle group-data-[disabled]:group-data-[selected]:text-surface',
                'group-data-[focus-visible]:ring-2 group-data-[focus-visible]:ring-primary group-data-[focus-visible]:ring-offset-2'
            )}
        >
            <Check
                className={classnames(
                    'opacity-0 group-data-[selected]:opacity-100 group-data-[indeterminate]:opacity-0',
                    size === 'sm' ? 'size-3' : 'size-3.5'
                )}
                strokeWidth={3}
            />
            <Minus
                className={classnames(
                    'opacity-0 group-data-[indeterminate]:opacity-100',
                    size === 'sm' ? 'size-3' : 'size-3.5'
                )}
                strokeWidth={3}
            />
        </span>
        {children && <span className={classnames('min-w-0', size === 'sm' ? 'text-sm' : 'text-base')}>{children}</span>}
    </>
);

const Checkbox = ({ value, className, children, size = 'md', ...props }: CheckboxProps) => (
    <AriaCheckbox
        {...props}
        isSelected={value ?? props.isSelected}
        className={checkboxClassName(classnames(size === 'sm' && 'gap-2.5', className))}
    >
        <CheckboxContent size={size}>{children}</CheckboxContent>
    </AriaCheckbox>
);

export default Checkbox;
